import { useMemo, useState } from "react";
import {
  Archive,
  ArrowUpDown,
  ArrowUpRight,
  Boxes,
  Clock3,
  History,
  ListPlus,
  MapPin,
  MessageSquarePlus,
  Package,
  PackageSearch,
  ShoppingCart,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Lead, LeadDirection } from "../leads/types";
import {
  formatCompactAmount,
  formatDateTime,
  formatQuantity,
  formatTotalUnitPrice,
  getFreshness,
  relativeTime,
  statusLabels,
} from "../leads/utils";
import { FreshnessBadge } from "../leads/LeadsView";

type DashboardSort = "updated" | "valid" | "price" | "quantity";

function compareDescendingNumbers(a: number | null, b: number | null) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

function Metric({
  label,
  value,
  icon: Icon,
  warning = false,
}: {
  label: string;
  value: number;
  icon: typeof Boxes;
  warning?: boolean;
}) {
  return (
    <div className="flex min-h-[76px] items-center justify-between gap-3 rounded-lg border bg-card px-3.5 py-3">
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className={`size-4 ${warning ? "text-amber-300" : ""}`} />
        {label}
      </span>
      <strong className="text-xl leading-none font-semibold tabular-nums">
        {value}
      </strong>
    </div>
  );
}

function ProductCard({
  lead,
  onSelect,
}: {
  lead: Lead;
  onSelect: (id: string) => void;
}) {
  const seller = lead.direction === "sell";
  const productName = [lead.brand, lead.model].filter(Boolean).join(" ") || "—";
  return (
    <button
      type="button"
      data-lead-card={lead.id}
      onClick={() => onSelect(lead.id)}
      className="group flex h-[288px] w-full flex-col overflow-hidden rounded-lg border bg-card p-4 text-left shadow-none outline-none transition hover:bg-secondary/60 focus-visible:ring-2 focus-visible:ring-ring/30"
    >
      <div className="flex shrink-0 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Badge
            className={
              seller
                ? "bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/10"
                : "bg-secondary text-secondary-foreground hover:bg-secondary"
            }
          >
            {seller ? "货主" : "买主"}
          </Badge>
          {lead.contact_avatar && (
            <img
              src={lead.contact_avatar}
              alt=""
              className="size-5 shrink-0 rounded border object-cover"
            />
          )}
          <span className="truncate text-sm font-semibold text-foreground">
            {lead.contact_name || lead.company || "—"}
          </span>
        </div>
        <FreshnessBadge lead={lead} />
      </div>

      <div className="mt-4 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3
            className="truncate text-base leading-6 font-semibold"
            title={productName}
          >
            {productName}
          </h3>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="size-3.5 shrink-0" />
            <span className="truncate">{lead.location || "—"}</span>
          </p>
        </div>
        <ArrowUpRight className="mt-1 size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
      </div>

      <div className="mt-3 flex h-5 shrink-0 items-center gap-1.5 overflow-hidden">
        {lead.sku && (
          <Badge variant="secondary" className="max-w-full truncate">
            {lead.sku}
          </Badge>
        )}
        {lead.status !== "active" && (
          <Badge variant="outline">{statusLabels[lead.status]}</Badge>
        )}
      </div>

      <div
        className="mt-4 flex h-5 shrink-0 items-center gap-2 text-sm font-medium"
        title="数量"
      >
        <Package className="size-4 shrink-0 text-muted-foreground" />
        <span className="sr-only">数量</span>
        <span className="truncate tabular-nums">{formatQuantity(lead)}</span>
      </div>
      <div className="mt-4 grid shrink-0 grid-cols-3 gap-3 border-t pt-3">
        <div className="min-w-0">
          <p className="text-[11px] whitespace-nowrap text-muted-foreground">
            {seller ? "货主单价" : "买主预算"}
          </p>
          <p
            className="mt-1 truncate text-base leading-6 font-semibold tabular-nums sm:text-lg"
            title={
              lead.price === null
                ? "价格面议"
                : formatCompactAmount(lead.price, lead.currency)
            }
          >
            {lead.price === null
              ? "价格面议"
              : formatCompactAmount(lead.price, lead.currency)}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-[11px] whitespace-nowrap text-muted-foreground">
            居间费/台
          </p>
          <p className="mt-1 truncate text-base leading-6 font-semibold tabular-nums sm:text-lg">
            {lead.estimated_fee_per_unit === null
              ? "—"
              : formatCompactAmount(lead.estimated_fee_per_unit, lead.currency)}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-[11px] whitespace-nowrap text-muted-foreground">
            总单价
          </p>
          <p className="mt-1 truncate text-xs leading-6 font-medium tabular-nums text-muted-foreground sm:text-sm">
            {formatTotalUnitPrice(lead)}
          </p>
        </div>
      </div>

      <div className="mt-auto flex shrink-0 items-center justify-between gap-3 pt-4 text-[11px] text-muted-foreground">
        <span
          className="flex items-center gap-1.5"
          title={`有效至 ${formatDateTime(lead.valid_until)}`}
        >
          <Clock3 className="size-3.5" />
          <span className="sr-only">有效至</span>
          {formatDateTime(lead.valid_until)}
        </span>
        <span
          className="flex items-center gap-1.5"
          title={`${relativeTime(lead.updated_at)}更新`}
        >
          <History className="size-3.5" />
          <span className="sr-only">更新时间</span>
          {relativeTime(lead.updated_at)}
        </span>
      </div>
    </button>
  );
}

export function Dashboard({
  leads,
  onSelect,
  onAgent,
  contactFilter = null,
  onClearContactFilter,
}: {
  leads: Lead[];
  onSelect: (id: string) => void;
  onAgent: () => void;
  contactFilter?: { id: string; name: string } | null;
  onClearContactFilter?: () => void;
}) {
  const [direction, setDirection] = useState<"all" | LeadDirection>("all");
  const [scope, setScope] = useState<"current" | "archived">("current");
  const [sort, setSort] = useState<DashboardSort>("updated");
  const data = useMemo(() => {
    const current = leads.filter((lead) =>
      ["fresh", "expiring"].includes(getFreshness(lead)),
    );
    const archived = leads.filter((lead) => getFreshness(lead) === "archived");
    const today = new Date().toDateString();
    return {
      current: [...current].sort((a, b) =>
        b.updated_at.localeCompare(a.updated_at),
      ),
      archived: [...archived].sort((a, b) =>
        b.updated_at.localeCompare(a.updated_at),
      ),
      sell: current.filter((lead) => lead.direction === "sell").length,
      buy: current.filter((lead) => lead.direction === "buy").length,
      expiring: current.filter((lead) => getFreshness(lead) === "expiring")
        .length,
      today: leads.filter(
        (lead) => new Date(lead.created_at).toDateString() === today,
      ).length,
    };
  }, [leads]);
  const scopedLeads = scope === "current" ? data.current : data.archived;
  const visible = useMemo(
    () =>
      scopedLeads
        .filter((lead) => direction === "all" || lead.direction === direction)
        .filter(
          (lead) => !contactFilter || lead.contact_id === contactFilter.id,
        )
        .sort((a, b) => {
          const recentFirst = b.updated_at.localeCompare(a.updated_at);
          if (sort === "valid")
            return a.valid_until.localeCompare(b.valid_until) || recentFirst;
          if (sort === "price")
            return compareDescendingNumbers(a.price, b.price) || recentFirst;
          if (sort === "quantity")
            return (
              compareDescendingNumbers(a.quantity, b.quantity) || recentFirst
            );
          return recentFirst;
        }),
    [direction, scopedLeads, sort, contactFilter],
  );
  const scopedSell = scopedLeads.filter(
    (lead) => lead.direction === "sell",
  ).length;
  const scopedBuy = scopedLeads.filter(
    (lead) => lead.direction === "buy",
  ).length;

  return (
    <>
      <section className="flex items-center justify-between gap-4">
        <h1 className="text-lg font-semibold">交易看板</h1>
        <Button
          size="icon"
          onClick={onAgent}
          aria-label="整理新消息"
          title="整理新消息"
        >
          <MessageSquarePlus />
        </Button>
      </section>

      <section
        aria-label="盘口概览"
        className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4"
      >
        <Metric label="有效出售" value={data.sell} icon={PackageSearch} />
        <Metric label="有效求购" value={data.buy} icon={ShoppingCart} />
        <Metric
          label="临期 / 待核验"
          value={data.expiring}
          icon={Clock3}
          warning
        />
        <Metric label="今日新增" value={data.today} icon={ListPlus} />
      </section>

      <section aria-labelledby="product-queue-title" className="mt-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 id="product-queue-title" className="text-sm font-semibold">
            {scope === "current" ? "当前盘口" : "归档区"}
          </h2>
          {contactFilter && (
            <Badge
              variant="secondary"
              className="gap-1.5"
              aria-label={`按货主筛选：${contactFilter.name}`}
            >
              货主 · {contactFilter.name}
              <button
                type="button"
                aria-label="清除货主筛选"
                title="清除货主筛选"
                className="ml-0.5 inline-flex size-4 items-center justify-center rounded-full hover:bg-background"
                onClick={onClearContactFilter}
              >
                <X className="size-3" />
              </button>
            </Badge>
          )}
          <div className="flex flex-wrap gap-2">
            <div
              className="flex h-9 items-center rounded-md bg-muted p-1"
              aria-label="看板区域"
            >
              <button
                type="button"
                aria-pressed={scope === "current"}
                onClick={() => setScope("current")}
                className={`h-7 rounded px-3 text-xs font-medium whitespace-nowrap transition ${scope === "current" ? "bg-card" : "text-muted-foreground hover:text-foreground"}`}
              >
                当前 {data.current.length}
              </button>
              <button
                type="button"
                aria-label={`归档区 ${data.archived.length}`}
                aria-pressed={scope === "archived"}
                onClick={() => setScope("archived")}
                className={`flex h-7 items-center gap-1.5 rounded px-3 text-xs font-medium whitespace-nowrap transition ${scope === "archived" ? "bg-card" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Archive className="size-3.5" />
                {data.archived.length}
                <span className="sr-only">归档区</span>
              </button>
            </div>
            <div
              className="flex h-9 items-center rounded-md bg-muted p-1"
              aria-label="看板方向筛选"
            >
              {(["all", "sell", "buy"] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  aria-pressed={direction === item}
                  onClick={() => setDirection(item)}
                  className={`h-7 rounded px-3 text-xs font-medium whitespace-nowrap transition ${direction === item ? "bg-card" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {item === "all"
                    ? `全部 ${scopedLeads.length}`
                    : item === "sell"
                      ? `货主 ${scopedSell}`
                      : `买主 ${scopedBuy}`}
                </button>
              ))}
            </div>
            <div className="relative h-9">
              <ArrowUpDown className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <select
                aria-label="排序方式"
                title="排序方式"
                value={sort}
                onChange={(event) =>
                  setSort(event.target.value as DashboardSort)
                }
                className="h-9 w-40 appearance-none rounded-md border bg-card pr-7 pl-8 text-xs font-medium outline-none transition focus-visible:ring-2 focus-visible:ring-ring/30"
              >
                <option value="updated">最近更新</option>
                <option value="valid">有效期最近</option>
                <option value="price">价格从高到低</option>
                <option value="quantity">数量从高到低</option>
              </select>
            </div>
          </div>
        </div>

        {visible.length ? (
          <div className="mt-4 grid items-stretch gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visible.map((lead) => (
              <ProductCard key={lead.id} lead={lead} onSelect={onSelect} />
            ))}
          </div>
        ) : (
          <div className="grid min-h-56 place-items-center border-y border-dashed p-6 text-center">
            <div>
              <Boxes className="mx-auto size-6 text-muted-foreground" />
              <h3 className="mt-3 text-sm font-medium">
                {scope === "current"
                  ? contactFilter
                    ? "该货主暂无有效盘口"
                    : "暂无有效盘口"
                  : "暂无归档"}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                className="mt-3"
                onClick={
                  scope === "current" ? onAgent : () => setScope("current")
                }
                aria-label={scope === "current" ? "打开 Agent" : "返回当前盘口"}
                title={scope === "current" ? "打开 Agent" : "返回当前盘口"}
              >
                {scope === "current" ? <MessageSquarePlus /> : <ArrowUpRight />}
              </Button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
